<script lang="ts">
  import * as ramda from 'ramda';

  import Checkbox from '../Checkbox/Checkbox.svelte';
  import Button from '$shared/atoms/Button/Button.svelte';
  import { ButtonStyle } from '$shared/constants/ButtonStyle';
  import Label from '$provider/constants/Label';

  type DataType = {
    list?: CommonList[];
    multiSelect?: boolean;
    groupBy?: string;
  };

  interface Props {
    data?: DataType;
    disabled?: boolean;
    onchange?: (list: CommonList[]) => void;
  }

  let { data = {}, disabled = false, onchange }: Props = $props();

  let list: CommonList[] = $state([]);
  let listMap = $state({});

  $effect(() => {
    list = [...data.list];
  });

  $effect(() => {
    const tempListMap = {};
    list.forEach((item) => {
      const groupBy = item.groupBy || 'default';
      if (!tempListMap[groupBy]) {
        tempListMap[groupBy] = [];
      }
      tempListMap[groupBy].push(item);
    });
    listMap = ramda.clone(tempListMap);
  });

  let nonDisabledList = $derived(list.filter((item) => !item.disabled));
  let selectAllActive = $derived(
    nonDisabledList.filter((item) => item.active).length !== nonDisabledList.length
  );
  let clearAllActive = $derived(nonDisabledList.filter((item) => item.active).length !== 0);

  const onClickList = (itemId: string): void => {
    if (!data?.list) {
      return;
    }
    const isMultipleSelectable = !!data?.multiSelect;
    if (!isMultipleSelectable) {
      list.forEach((item) => {
        if (item.id !== itemId) {
          item.active = false;
        }
      });
    }
    const index = list.findIndex((item) => item.id === itemId);
    list[index].active = !list[index].active;
    list = [...list];
  };

  const onClickToggleAll = (status = false): void => {
    if (!data?.list) {
      return;
    }
    list.forEach((item) => {
      if (item.disabled) return;
      item.active = status;
    });
    list = [...list];
  };

  const onClickApply = (): void => {
    onchange?.(list);
  };
</script>

<div class="dropdown-list-container">
  <div class="dropdown-list">
    {#each Object.entries(listMap) as [key, value], i (i)}
      {#if key !== 'default'}
        <div data-cy={key} class="group-title">{key}</div>
      {/if}
      <!-- content here -->
      {#each value || [] as item, index (index)}
        <button
          data-cy={item.id}
          disabled={disabled || item.disabled}
          class="dropdown-list-i"
          onclick={() => onClickList(item.id)}
        >
          <div class="checkbox">
            <Checkbox
              name={`${item.id}-checkbox-${index}`}
              checked={item.active}
              disabled={disabled || item.disabled}
            />
            <div class="label" hidden={!item.label}>{item.label}</div>
            {#if item.componentInfo && item.componentInfo.component}
              <item.componentInfo.component {...item.componentInfo.props || {}} />
            {/if}
          </div>
          {#if item.key}
            <div class="sub">{item.key}</div>
          {/if}
        </button>
      {/each}
    {/each}
  </div>
  <div class="actions">
    <div class="left-action">
      <Button style={ButtonStyle.Primary} onclick={onClickApply} name="apply-btn">
        {Label.Apply}
      </Button>
    </div>
    <div class="right-action">
      <!-- Show "Select all" and "Clear all" buttons only when:
           - Multi-select mode is enabled
           - List exists and has more than 5 items (to avoid clutter for short lists) -->
      {#if data?.multiSelect && data?.list && data.list.length > 5}
        <div class="options">
          <button
            data-cy="selectAll"
            class="option"
            disabled={!selectAllActive || disabled}
            onclick={() => onClickToggleAll(true)}>Select all</button
          >
          <button
            data-cy="clearAll"
            class="option"
            disabled={!clearAllActive || disabled}
            onclick={() => onClickToggleAll()}>Clear all</button
          >
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .dropdown-list-container {
    box-shadow:
      0px 3px 11px 1px rgba(0, 0, 0, 0.0783708),
      0px 7px 18px 3px rgba(0, 0, 0, 0.1);
  }
  .dropdown-list {
    min-width: 200px;
    padding: 16px;
    border-radius: 3px;
    max-height: calc(370px - 48px);
    overflow: auto;
  }
  .group-title {
    font-weight: bold;
    font-size: 16px;
    line-height: 22px;
    margin-bottom: 14px;
    color: var(--grey-dark);
  }
  .dropdown-list-i {
    width: 100%;
  }
  .dropdown-list-i:not(:last-child) {
    margin-bottom: 16px;
  }
  .dropdown-list-i .checkbox {
    position: relative;
    margin-right: 8px;
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
  .dropdown-list-i .checkbox::before {
    content: '';
    width: 100%;
    height: 100%;
    background-color: transparent;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }
  .dropdown-list-i .sub {
    margin-left: 24px;
    text-align: left;
    font-weight: normal;
    font-size: 11px;
    line-height: 18px;
    color: var(--grey-medium);
    white-space: pre;
  }
  .dropdown-list-i:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .actions {
    padding: 16px;
    width: 100%;
    background-color: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .left-action {
    display: flex;
    align-items: center;
    height: 100%;
  }
  .right-action {
    margin-left: 14px;
  }
  .options {
    display: flex;
    align-items: center;
    width: max-content;
  }
  .options .option {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 18px;
    font-weight: normal;
    color: var(--blue);
  }
  .options .option:disabled {
    color: var(--grey-light);
    cursor: not-allowed;
  }
  .options .option:not(:last-child) {
    padding-right: 16px;
  }
</style>
